/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';

export type DnDItem = { id: string };

export type DndParent<T extends DnDItem> = DnDItem & {
  id: string;
  items: T[];
  notDroppable?: boolean;
};

const reorder = <T>(list: T[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export type UseDnDParams<
  TParent extends DndParent<TChild>,
  TChild extends DnDItem,
> = {
  items: TParent[];
  onChange: (items: TParent[]) => void;
};

export const useDnD = <
  TParent extends DndParent<TChild>,
  TChild extends DnDItem,
>(
  p: UseDnDParams<TParent, TChild>,
) => {
  const [_items, _setItems] = useState(p.items);
  const itemsRef = useRef(p.items);
  const onChangeRef = useRef(p.onChange);

  itemsRef.current = p.items;
  onChangeRef.current = p.onChange;

  const setItems: React.Dispatch<React.SetStateAction<TParent[]>> = useCallback(
    (calcItems) => {
      const nextItems =
        typeof calcItems === 'function'
          ? calcItems(itemsRef.current)
          : calcItems;
      itemsRef.current = nextItems;
      _setItems(nextItems);
      onChangeRef.current(nextItems);
    },
    [],
  );

  useEffect(() => {
    itemsRef.current = p.items;
    _setItems(p.items);
  }, [JSON.stringify(p.items)]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      if (result.type === 'droppableItem') {
        setItems((prev) => reorder(prev, sourceIndex, destIndex));
        return;
      }

      if (result.type === 'droppableSubItem') {
        setItems((prev) => {
          const newItems = [...prev];

          const sourceParentId = result.source.droppableId;
          const destParentId = result?.destination?.droppableId;

          const sourceParentIndex = newItems.findIndex(
            (v) => v.id === sourceParentId,
          );
          const destParentIndex = newItems.findIndex(
            (v) => v.id === destParentId,
          );

          if (sourceParentIndex === -1 || destParentIndex === -1)
            return newItems;

          const sourceParent = newItems[sourceParentIndex];
          const destParent = newItems[destParentIndex];

          const movingBetweenDifferentParents = sourceParentId !== destParentId;

          if (destParent.notDroppable && movingBetweenDifferentParents)
            return newItems;

          const sourceItems = Array.from(sourceParent.items);
          const destItems = Array.from(destParent.items);

          if (sourceParentId === destParentId) {
            newItems[sourceParentIndex] = {
              ...sourceParent,
              items: reorder(sourceItems, sourceIndex, destIndex),
            };
          } else {
            const [movedChild] = sourceItems.splice(sourceIndex, 1);
            destItems.splice(destIndex, 0, movedChild);
            newItems[sourceParentIndex] = {
              ...sourceParent,
              items: sourceItems,
            };
            newItems[destParentIndex] = { ...destParent, items: destItems };
          }

          return newItems;
        });
      }
    },
    [setItems],
  );

  return { items: p.items, setItems, handleDragEnd };
};
